import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { getCSRFToken, clearCSRFToken } from "./csrf";

export const STALE_TIMES = {
  STATIC: 30 * 60 * 1000,
  SEMI_STATIC: 10 * 60 * 1000,
  DYNAMIC: 2 * 60 * 1000,
  REAL_TIME: 30 * 1000,
  USER_DATA: 5 * 60 * 1000,
} as const;

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const headers: Record<string, string> = data ? { "Content-Type": "application/json" } : {};
  
  if (!["GET", "HEAD", "OPTIONS"].includes(method.toUpperCase())) {
    const token = await getCSRFToken();
    headers["X-CSRF-Token"] = token;
  }

  const res = await fetch(url, {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });

  if (res.status === 403) {
    const clone = res.clone();
    const error = await clone.json().catch(() => ({}));
    if (error.error?.includes("CSRF")) {
      clearCSRFToken();
    }
  }

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const res = await fetch(queryKey.join("/") as string, {
      credentials: "include",
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: STALE_TIMES.USER_DATA,
      gcTime: STALE_TIMES.SEMI_STATIC,
      retry: (failureCount, error) => {
        if (error instanceof Error && error.message.startsWith("4")) {
          return false;
        }
        return failureCount < 2;
      },
    },
    mutations: {
      retry: false,
    },
  },
});
